import React from 'react';
import { Card, Badge, Button, UncontrolledTooltip } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Briefcase, FileSpreadsheet, Handshake, Eye, Globe } from 'lucide-react';
import firebase from 'firebase/compat/app';
import { Bid } from './BidShared'; // Import the existing Bid type
import { Link } from 'react-router-dom';

interface ContractorMatchInfo {
  id: string;
  hasViewed: boolean;
  hasBid: boolean;
  isSelected: boolean;
  isInterested: boolean;
  status: string;
  dtInvited?: firebase.firestore.Timestamp | null;
  dtViewed?: firebase.firestore.Timestamp | null;
  lastViewedAt?: firebase.firestore.Timestamp | null;
}

interface ContractorCardProps {
  contractor: {
    id: string;
    contractorId: string;
    businessName: string;
    email: string;
    phone: string;
    website?: string;
    logo?: string;
    categories?: string[];
    generalInfo?: string;
    matchInfo?: ContractorMatchInfo;
  };
  bid?: Bid; // Use the existing Bid type
  isSelected?: boolean;
  onSelect?: (contractorId: string, isSelected: boolean) => void;
  onViewBid?: (bidId: string) => void;
  onAcceptBid?: (bid: Bid) => void; // Use the existing Bid type
  onDeleteBid?: (bid: Bid) => void; // Use the existing Bid type
  disabled?: boolean;
  withCheckbox?: boolean;
  contractId?: string | null; // Updated to allow null
}

const formatDate = (timestamp: firebase.firestore.Timestamp | null | undefined): string => {
  if (!timestamp) return 'Not yet';
  return timestamp.toDate().toLocaleString();
};

const ContractorCard: React.FC<ContractorCardProps> = ({
  contractor,
  bid,
  isSelected = false,
  onSelect,
  onViewBid,
  onAcceptBid,
  onDeleteBid,
  disabled = false,
  withCheckbox = false,
  contractId, // Added contractId parameter
}) => {
  // Determine card border color based on status
  let borderClass = '';
  if (contractor.matchInfo?.hasBid) {
    borderClass = 'border-success';
  } else if (contractor.matchInfo?.status === 'invited') {
    borderClass = 'border-info';
  }

  // Determine badge for status
  let statusBadge = null;
  if (contractor.matchInfo?.hasBid) {
    statusBadge = <div className="badge bg-success">Accepted</div>;
  } else if (contractor.matchInfo?.status === 'invited') {
    statusBadge = <div className="badge bg-info">{bid?.status || contractor.matchInfo?.status}</div>;
  }

  return (
    <Card className={`h-100 p-3 ${borderClass}`}>
      <div className="d-flex justify-content-between align-items-start">
        <div className="d-flex">
          {contractor.logo ? (
            <img
              src={contractor.logo}
              alt={contractor.businessName}
              className="rounded-circle object-fit-cover me-3"
              style={{ width: "48px", height: "48px" }}
            />
          ) : (
            <div
              className="flex-shrink-0 me-3 rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
              style={{ width: "48px", height: "48px" }}
            >
              <Briefcase />
            </div>
          )}
          <div>
            <h6 className="mb-1">{contractor.businessName}</h6>
            {contractor.website && (
              <div className="mb-1">
                <Globe size={14} className="me-2" />
                <a
                  href={contractor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                >
                  {contractor.website.replace(/^https?:\/\//, "")}
                </a>
              </div>
            )}
            {contractor.categories && !bid && (
              <div className="mt-1">
                {contractor.categories.map(category => (
                  <span key={category} className="me-2 mb-1 badge bg-secondary">
                    {category}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-2">
              <a
                href={`/contractor-profile/${contractor.contractorId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-primary btn-sm"
              >
                View Profile
              </a>
            </div>
          </div>
        </div>
        {withCheckbox && (
          <div className="form-check form-check-sm">
            <input
              className="form-check-input"
              type="checkbox"
              checked={isSelected}
              onChange={() => onSelect && onSelect(contractor.contractorId, !isSelected)}
              disabled={disabled || contractor.matchInfo?.hasBid || contractor.matchInfo?.status === 'invited'}
            />
          </div>
        )}
        {statusBadge}
      </div>

      {/* General info for all contractors */}
      {contractor.generalInfo && !bid && (
        <div className="mt-2">
          <small className="text-muted">
            {contractor.generalInfo.length > 150
              ? `${contractor.generalInfo.substring(0, 150)}...`
              : contractor.generalInfo}
          </small>
        </div>
      )}

      {/* Show bid information if available */}
      {bid && (
        <div className="mt-3 border-top pt-2">
          <div className="d-flex justify-content-between mb-1">
            <small><strong>Bid Amount:</strong></small>
            <small>${bid.bidAmount}</small>
          </div>
          <div className="d-flex justify-content-between mb-1">
            <small><strong>Timeline:</strong></small>
            <small>{bid.startDate} - {bid.endDate}</small>
          </div>
          <div className="d-flex justify-content-between mb-1">
            <small><strong>Inspection:</strong></small>
            <small>
              <Badge
                color={bid.requireInspection ? 'warning' : 'success'} // Changed from 'success' : 'danger'
                className="small-badge"
              >
                {bid.requireInspection ? 'Yes' : 'No'}
              </Badge>
            </small>
          </div>
          <div className="d-flex justify-content-between mb-1">
            <small><strong>Warranty:</strong></small>
            <small>{bid.warrantyPeriod} days</small>
          </div>
          {bid.approach && (
            <div className="mb-2 mt-2">
              <small><strong>Approach:</strong></small>
              <p className="small mb-1">{bid.approach}</p>
            </div>
          )}
          <div className="d-flex justify-content-between mt-2">
            <Button
              color="info"
              size="sm"
              onClick={() => onViewBid && bid.id && onViewBid(bid.id)}
              disabled={!bid.id || disabled}
            >
              <FileSpreadsheet size={14} /> View
            </Button>
            {bid.status === 'Accepted' ? (
              contractId ? (
                <Link to={`/contract/${contractId}`} className="btn btn-success me-2 view-contract-button">
                  <Handshake size={14} /> View Contract
                </Link>
              ) : (
                <Badge color="success">Accepted</Badge>
              )
            ) : (
              <>
                <Button
                  color={bid.requireInspection ? "warning" : "success"}
                  size="sm"
                  onClick={() => onAcceptBid && onAcceptBid(bid)}
                  disabled={disabled}
                  id={`accept-bid-${contractor.id}`}
                >
                  <Handshake size={14} /> Accept
                </Button>
                <UncontrolledTooltip
                  placement="top"
                  target={`accept-bid-${contractor.id}`}
                  transition={{ timeout: 150 }} >
                  {bid.requireInspection
                    ? "Accept this bid, and this will create a contract with an inspection contingency that must be removed prior to the work commencing."
                    : "Accept this bid, and this will create a non-contingent contract to have the work performed."}
                </UncontrolledTooltip>
              </>
            )}
          </div>
        </div>
      )}

      {/* Show match information */}
      {contractor.matchInfo && !bid && (
        <div className="mt-3">
          {(contractor.matchInfo.dtInvited || contractor.matchInfo.status === 'invited') && (
            <small className="d-block">
              <strong>Invited:</strong>{' '}
              {formatDate(contractor.matchInfo.dtInvited)}
            </small>
          )}
          <small className="d-block">
            <strong>Viewed:</strong>{' '}
            {contractor.matchInfo.dtViewed && (
              <Eye size={14} className="text-primary ms-1 me-1" />
            )}
            {formatDate(contractor.matchInfo.dtViewed)}
          </small>
          {contractor.matchInfo.isInterested && (
            <div className="mt-1">
              <Badge color="warning">Interested in job</Badge>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default ContractorCard;